import React, { useState, useEffect, useRef } from "react";
import { View, StyleSheet, Modal } from "react-native";
import { Text, Button, Surface, Portal } from "react-native-paper";
import { theme } from "../theme/theme";

/**
 * AlertModal - A reusable alert modal component that matches the app theme
 * 
 * @param {boolean} visible - Whether the modal is visible
 * @param {string} title - Title text for the alert
 * @param {string} message - Message text for the alert
 * @param {string} buttonText - Text for the primary button (default: "OK")
 * @param {function} onDismiss - Callback when the button is pressed or modal is dismissed
 * @param {boolean} cancelable - Whether the modal can be dismissed by tapping outside (default: true)
 * @param {string} [cancelText] - Supplying this turns the alert into a question with two buttons
 * @param {function} [onConfirm] - Run when the primary button is pressed, before dismissing
 * @param {boolean} [destructive] - Colours the primary button as a destructive action
 */
const AlertModal = ({
  visible,
  title,
  message,
  buttonText = "OK",
  onDismiss,
  cancelable = true,
  cancelText,
  onConfirm,
  destructive = false,
}) => {
  const [displayTitle, setDisplayTitle] = useState("");
  const [displayMessage, setDisplayMessage] = useState("");
  const timeoutRef = useRef(null);
  const onDismissRef = useRef(onDismiss);

  // Keep onDismiss ref updated
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  // Update display content when visible becomes true or content changes
  useEffect(() => {
    if (visible) {
      // Clear any pending timeout immediately - this is critical
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Update displayed content immediately
      setDisplayTitle(title || "");
      setDisplayMessage(message || "");
    }
  }, [visible, title, message]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    handleDismiss();
  };

  const handleDismiss = () => {
    // Clear any existing timeout first
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Small delay to allow fade animation to complete before clearing content
    timeoutRef.current = setTimeout(() => {
      if (onDismissRef.current) {
        onDismissRef.current();
      }
      timeoutRef.current = null;
    }, 200); // Match fade animation duration
  };

  // Always render the Portal/Modal structure, but control visibility through Modal's visible prop
  // This ensures React Native's Modal component properly handles the visibility state
  return (
    <Portal>
      <Modal
        visible={visible}
        animationType="fade"
        transparent={true}
        onRequestClose={cancelable ? handleDismiss : undefined}
      >
        <View style={styles.overlay}>
          <Surface style={styles.content} elevation={8}>
            {displayTitle && (
              <Text style={styles.title} accessibilityRole="header">
                {displayTitle}
              </Text>
            )}
            {displayMessage && (
              <Text style={styles.message} accessibilityRole="text">
                {displayMessage}
              </Text>
            )}
            <View style={cancelText ? styles.buttonRow : undefined}>
              {cancelText ? (
                <Button
                  mode="outlined"
                  onPress={handleDismiss}
                  style={styles.cancelButton}
                  contentStyle={styles.buttonContent}
                  accessible={true}
                  accessibilityLabel={`${cancelText} button`}
                  accessibilityRole="button"
                >
                  {cancelText}
                </Button>
              ) : null}
              <Button
                mode="contained"
                onPress={onConfirm ? handleConfirm : handleDismiss}
                style={[styles.button, destructive && styles.destructiveButton]}
                contentStyle={styles.buttonContent}
                accessible={true}
                accessibilityLabel={`${buttonText} button`}
                accessibilityRole="button"
              >
                {buttonText}
              </Button>
            </View>
          </Surface>
        </View>
      </Modal>
    </Portal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing.lg,
  },
  content: {
    backgroundColor: theme.colors.surface,
    borderRadius: 16,
    padding: theme.spacing.xl,
    minWidth: 280,
    maxWidth: 400,
    alignItems: "center",
  },
  title: {
    fontSize: 20,
    fontWeight: "600",
    color: theme.colors.onSurface,
    fontFamily: "Poppins",
    textAlign: "center",
    marginBottom: theme.spacing.md,
  },
  message: {
    fontSize: 16,
    color: theme.colors.onSurfaceVariant,
    fontFamily: "Poppins",
    textAlign: "center",
    marginBottom: theme.spacing.xl,
    lineHeight: 22,
  },
  button: {
    borderRadius: 12,
    backgroundColor: theme.colors.primary,
    minWidth: 120,
  },
  buttonContent: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xl,
  },
  buttonRow: {
    flexDirection: "row",
    justifyContent: "center",
    gap: theme.spacing.sm,
  },
  cancelButton: {
    borderRadius: 12,
    borderColor: theme.colors.primary,
  },
  destructiveButton: {
    backgroundColor: theme.colors.error,
  },
});

export default AlertModal;
